import classNames from 'classnames';
import React, { RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { HasClassName } from '../component-utils';
import { useSelector } from '../hook-utils';
import { actions } from '../store';
import { selectSplitterValue } from '../store/reducers/localSettings';
import './Splitter.scss';

interface SplitterContextData {
  value: number
  onChange(pos: Point): void
}

interface Point {
  left: number
  top: number
}

const SplitterContext = React.createContext<SplitterContextData | null>(null)

type Props = HasClassName & {
  vertical?: boolean
} & ({
  value?: undefined
  onChange?: undefined
  disabledWidth: string
} | {
  value: number
  onChange(value: number): void
  disabledWidth: string
})

export function Splitter({ className, children, value, onChange, vertical, disabledWidth }: React.PropsWithChildren<Props>) {
  const startRef = useRef<HTMLDivElement>(null)
  const endRef = useRef<HTMLDivElement>(null)
  const onChangeRef = useRef(onChange)

  useCallback(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const getUncontrolledValue = useCallback(() => {
    return value === undefined ? 0.5 : value;
  }, [value])

  useEffect(() => {
    setUncontrolledValue(getUncontrolledValue())
  }, [getUncontrolledValue])
  const [uncontrolledValue, setUncontrolledValue] = useState(getUncontrolledValue())

  const ctx = useMemo(() => ({
    value: uncontrolledValue,
    onChange(pos: Point) {
      if (!startRef.current || !endRef.current) { return }

      const c1 = getCoord(startRef.current.getBoundingClientRect(), vertical)
      const c2 = getCoord(pos, vertical)
      const c3 = getCoord(endRef.current.getBoundingClientRect(), vertical)

      const newValue = (c2 - c1) / (c3 - c1)

      if (onChangeRef.current) {
        onChangeRef.current(newValue)
      } else {
        setUncontrolledValue(newValue)
      }
    },
  }), [uncontrolledValue, vertical])

  const style = useMemo(() => {
    if(value === 1 && disabledWidth && disabledWidth !== ''){
      return  { '--value': disabledWidth } as any
    }
    return  { '--value': `${uncontrolledValue * 100}%` } as any
    
  }, [disabledWidth, uncontrolledValue, value])

  return <div className={classNames('Splitter', vertical && 'vertical', className)} style={style}>
    <SplitterContext.Provider value={ctx}>
      <div ref={startRef} className='ref' />
      {children}
      <div ref={endRef} className='ref' />
    </SplitterContext.Provider>
  </div>
}

function getCoord(point: Point, vertical: boolean | undefined) {
  return vertical ? point.top : point.left
}

interface SplitterDividerProps extends HasClassName {
  divRef?: RefObject<HTMLDivElement>
}

export function SplitterDivider({ className, divRef }: SplitterDividerProps) {
  const ctx = useContext(SplitterContext)
  const posRef = useRef<Point>()
  const onChangeRef = useRef(ctx ? ctx.onChange : null)

  useEffect(() => {
    onChangeRef.current = ctx ? ctx.onChange : null
  })

  useEffect(() => {
    const ref = divRef
    if (ref && ref.current !== null) {
      ref.current.addEventListener('mouseup', onMouseUp)
      ref.current.addEventListener('mousemove', onMouseMove)
    } else {
      document.addEventListener('mouseup', onMouseUp)
      document.addEventListener('mousemove', onMouseMove)
    }

    return () => {
      if (ref && ref.current !== null) {
        ref.current.removeEventListener('mouseup', onMouseUp)
        ref.current.removeEventListener('mousemove', onMouseMove)
      } else {
        document.removeEventListener('mouseup', onMouseUp)
        document.removeEventListener('mousemove', onMouseMove)
      }
    }

    function onMouseMove(e: MouseEvent) {
      if (!posRef.current || !onChangeRef.current) { return }
      e.preventDefault()

      onChangeRef.current({
        left: e.pageX + posRef.current.left,
        top: e.pageY + posRef.current.top,
      })
    }

    function onMouseUp(e: MouseEvent) {
      posRef.current = undefined
    }
  }, [divRef])

  if (!ctx) {
    console.warn('<SplitterDivider> should not be used outside a <Splitter>')
    return null
  }

  function onMouseDown(e: React.MouseEvent) {
    e.preventDefault()
    const bounds = e.currentTarget.getBoundingClientRect()
    posRef.current = {
      left: bounds.left - e.pageX,
      top: bounds.top - e.pageY,
    }
  }

  return <div className={classNames('SplitterDivider', className)} onMouseDown={onMouseDown} />
}

type StoredSplitterProps = React.PropsWithChildren<HasClassName> & {
  storeKey: string
  initialValue?: number
  enable?: boolean
  disabledWidth?:string
}

export function StoredSplitter({ className, storeKey, children, initialValue = 0.5, enable = true, disabledWidth = '' }: StoredSplitterProps) {
  const value = useSelector(s => selectSplitterValue(s, storeKey))
  const dispatch = useDispatch()

  function onChange(newValue: number) {
    dispatch(actions.setSplitter(storeKey, newValue))
  }

  return <Splitter className={className} value={!enable ? 1 : value === undefined ? initialValue : value} disabledWidth={disabledWidth} onChange={onChange}>
    {children}
  </Splitter>
}
